// Shared formatting helpers for dates, times, and currency
// These helpers are intentionally tolerant: if regional settings are missing
// they fall back to sensible defaults.

import type { RegionalSettings } from '@/contexts/context';

const FALLBACK_LOCALE = 'en-US';

const resolveLocale = (language?: string) => {
  if (!language) {
    if (typeof navigator !== 'undefined') {
      return navigator.language || FALLBACK_LOCALE;
    }
    return FALLBACK_LOCALE;
  }

  // Map short language code to a reasonable locale
  switch (language) {
    case 'en':
      return 'en-US';
    case 'es':
      return 'es-ES';
    case 'fr':
      return 'fr-FR';
    case 'de':
      return 'de-DE';
    case 'ja':
      return 'ja-JP';
    default:
      return `${language}-${language.toUpperCase()}`;
  }
};

// Map our simple timezone values to proper IANA timezone identifiers
const resolveTimezone = (timezone?: string): string => {
  if (!timezone) {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  }

  // Map simple UTC offset strings to IANA timezones
  switch (timezone.toLowerCase()) {
    case 'utc+0':
    case 'utc':
      return 'UTC';
    case 'utc-8':
      return 'America/Los_Angeles'; // PST/PDT
    case 'utc-5':
      return 'America/New_York'; // EST/EDT
    case 'utc+1':
      return 'Europe/Paris'; // CET/CEST
    case 'utc+9':
      return 'Asia/Tokyo'; // JST
    default:
      // If it's already a valid IANA timezone, use it
      return timezone;
  }
};

export function formatDate(
  value: string | number | Date,
  regional: RegionalSettings,
): string {
  const locale = resolveLocale(regional.language);
  const timezone = resolveTimezone(regional.timezone);
  const date = value instanceof Date ? value : new Date(value);

  try {
    // Use Intl to respect locale digits & timezone, but
    // explicitly control the order based on regional.dateFormat.
    const dtf = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: timezone,
    });

    const parts = dtf.formatToParts(date);
    const day = parts.find((p) => p.type === 'day')?.value ?? '';
    const month = parts.find((p) => p.type === 'month')?.value ?? '';
    const year = parts.find((p) => p.type === 'year')?.value ?? '';

    const sep =
      regional.dateFormat === 'ymd' || regional.dateFormat === 'iso' ? '-' : '/';

    let result: string;
    switch (regional.dateFormat) {
      case 'dmy':
        result = [day, month, year].join(sep);
        break;
      case 'ymd':
        result = [year, month, day].join(sep);
        break;
      case 'iso':
        // Strict ISO-style ordering; year/month/day are already 2-digit where needed.
        result = [year, month, day].join('-');
        break;
      case 'mdy':
      default:
        result = [month, day, year].join(sep);
        break;
    }

    return result;
  } catch (err) {
    return date.toLocaleDateString();
  }
}

export function formatDateTime(
  value: string | number | Date,
  regional: RegionalSettings,
): string {
  const locale = resolveLocale(regional.language);
  const timezone = resolveTimezone(regional.timezone);
  const date = value instanceof Date ? value : new Date(value);

  try {
    // Re-use formatDate so the date part always matches the
    // configured dateFormat, then append a time portion.
    const datePart = formatDate(date, regional);

    const timeFormatter = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: regional.timeFormat === '12h',
      timeZone: timezone,
    });

    const timePart = timeFormatter.format(date);

    return `${datePart} ${timePart}`;
  } catch (err) {
    return date.toLocaleString();
  }
}

export function formatCurrency(
  amount: number | null | undefined,
  regional: RegionalSettings,
): string {
  if (amount == null || isNaN(amount as any)) return '—';

  const locale = resolveLocale(regional.language);
  const currency = regional.currency?.toUpperCase() || 'USD';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `$${amount.toLocaleString()}`;
  }
}

export function formatNumber(
  value: number | null | undefined,
  regional: RegionalSettings,
): string {
  if (value == null || isNaN(value as any)) return '0';
  const locale = resolveLocale(regional.language);

  try {
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return value.toLocaleString();
  }
}

